import { useAnalytics } from '@pco/analytics';
import type { ProductDetailsImplProps } from '@pco/modules/ProductDetails/index.types';
import type { Vehicle } from '@pco/modules/VehicleSelection';
import { useVehiclesSelection } from '@pco/modules/VehicleSelection/useVehicleSelection';
import { FitmentMessage } from '@pco/molecules/FitmentMessage';
import type { ProductAttribute } from '@pco/types/product';
import {
	isProductExactFit,
	isProductUniversalFit,
	isProductUnknownFitment,
} from '@pco/utils/product';
import { useMemo } from 'react';

export type UseFitmentLabelsCMSProps = Pick<
	ProductDetailsImplProps,
	| 'checkToSeeIfItFitsMainText'
	| 'exactFitMainText'
	| 'mayNotFitMainText'
	| 'compatibleWithMostMainText'
	| 'checkToSeeIfItFitsSubText'
	| 'exactFitSubText'
	| 'mayNotFitSubText'
	| 'compatibleWithMostSubText'
	| 'fitmentUnavailableMainText'
	| 'fitmentUnavailableSubText'
>;
export type FitmentType =
	| 'ExactFit'
	| 'CompatibleWithMost'
	| 'CheckToSeeIfItFits'
	| 'MayNotFit'
	| 'NotAvailable';

export type FitmentLabels = {
	[K in FitmentType]?: {
		mainText: string;
		subText: string;
	};
};

type UseFitmentLabelsProps = {
	cmsData: UseFitmentLabelsCMSProps;
};

export const useFitmentLabels = ({
	cmsData,
}: UseFitmentLabelsProps): FitmentLabels => {
	return useMemo(() => {
		return {
			CheckToSeeIfItFits: {
				mainText: cmsData.checkToSeeIfItFitsMainText,
				subText: cmsData.checkToSeeIfItFitsSubText,
			},
			CompatibleWithMost: {
				mainText: cmsData.compatibleWithMostMainText,
				subText: cmsData.compatibleWithMostSubText,
			},
			ExactFit: {
				mainText: cmsData.exactFitMainText,
				subText: cmsData.exactFitSubText,
			},
			MayNotFit: {
				mainText: cmsData.mayNotFitMainText,
				subText: cmsData.mayNotFitSubText,
			},
			NotAvailable: {
				mainText: cmsData.fitmentUnavailableMainText,
				subText: cmsData.fitmentUnavailableSubText,
			},
		};
	}, [cmsData]);
};

export const getFitmentType = (
	attributes: ProductAttribute[],
	vehicle?: Vehicle | null,
): FitmentType => {
	if (!vehicle) return 'CheckToSeeIfItFits';
	if (isProductUniversalFit(attributes)) return 'CompatibleWithMost';
	if (isProductUnknownFitment(attributes)) return 'NotAvailable';
	if (isProductExactFit(attributes, vehicle.modelYearCode)) return 'ExactFit';

	return 'MayNotFit';
};
const useGetFitment = (
	productAttributes: ProductAttribute[] = [],
	vehicle?: Vehicle | null,
	fitmentLabels?: FitmentLabels,
) => {
	const { setVehicleDrawerOpen } = useVehiclesSelection();
	const fireAnalytics = useAnalytics('FitmentMessage');
	// biome-ignore lint/correctness/useExhaustiveDependencies: useAnalytics isn't returning a stable function
	return useMemo(() => {
		const fitmentType: FitmentType = getFitmentType(productAttributes, vehicle);
		const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
			e.stopPropagation();
			e.preventDefault();
			if (fitmentType === 'CheckToSeeIfItFits') {
				fireAnalytics('openFitment', {
					labels: fitmentLabels?.[fitmentType],
				});
				setVehicleDrawerOpen(true, 'list');
			}
		};
		switch (fitmentType) {
			case 'ExactFit':
				return (
					<FitmentMessage.ExactFit
						{...fitmentLabels?.[fitmentType]}
						vehicle={vehicle}
					/>
				);
			case 'CompatibleWithMost':
				return (
					<FitmentMessage.CompatibleWithMost
						{...fitmentLabels?.[fitmentType]}
						vehicle={vehicle}
					/>
				);
			case 'CheckToSeeIfItFits':
				return (
					<FitmentMessage.CheckToSeeIfItFits
						{...fitmentLabels?.[fitmentType]}
						onClick={handleClick}
					/>
				);
			case 'MayNotFit':
				return (
					<FitmentMessage.MayNotFit
						{...fitmentLabels?.[fitmentType]}
						vehicle={vehicle}
					/>
				);
			case 'NotAvailable':
				return (
					<FitmentMessage.NotAvailable {...fitmentLabels?.[fitmentType]} />
				);
			default:
				return null;
		}
	}, [vehicle, productAttributes, setVehicleDrawerOpen, fitmentLabels]);
};

export default useGetFitment;
